import { useEffect, useState } from 'react';
import { User } from 'react-feather';
import { resolveMediaUrl } from '@/core/api-client/client';
import './UserAvatar.scss';

function initialsFromName(name: string | null | undefined): string {
  const parts = (name ?? '')
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
}

interface Props {
  fullName?: string | null;
  avatarUrl?: string | null;
  size?: number;
  className?: string;
  /** When no photo (or load fails): initials (default) or user icon. */
  fallback?: 'initials' | 'icon';
}

/** Profile photo or initials/icon fallback for header / account page. */
export function UserAvatar({
  fullName,
  avatarUrl,
  size = 40,
  className,
  fallback = 'initials',
}: Props) {
  const src = resolveMediaUrl(avatarUrl);
  const [broken, setBroken] = useState(false);
  useEffect(() => {
    setBroken(false);
  }, [src]);
  const style = {
    width: size,
    height: size,
    fontSize: Math.max(12, Math.round(size * 0.36)),
  };

  if (src && !broken) {
    return (
      <img
        className={`rcats-user-avatar rcats-user-avatar--image ${className ?? ''}`.trim()}
        src={src}
        alt={fullName ? `${fullName} avatar` : 'Profile avatar'}
        style={style}
        onError={() => setBroken(true)}
      />
    );
  }

  if (fallback === 'icon') {
    const iconSize = Math.max(14, Math.round(size * 0.5));
    return (
      <span
        className={`rcats-user-avatar rcats-user-avatar--icon ${className ?? ''}`.trim()}
        style={style}
        aria-hidden
      >
        <User size={iconSize} strokeWidth={2} />
      </span>
    );
  }

  return (
    <span
      className={`rcats-user-avatar rcats-user-avatar--initials ${className ?? ''}`.trim()}
      style={style}
      aria-hidden
    >
      {initialsFromName(fullName)}
    </span>
  );
}
